import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { useSubmitContact } from "@/api/hooks/mutations";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { Field, fieldInputClass } from "@/components/ui/Field";

const contactSchema = z.object({
  name: z.string().min(2, "Tell us your name"),
  email: z.string().email("Enter a valid email"),
  company: z.string().max(200).optional().or(z.literal("")),
  interest: z.enum(["outstaffing", "recruiting", "both", "job"]),
  message: z.string().min(5, "A short message helps"),
  // Honeypot — must stay empty.
  website: z.string().max(0).optional().or(z.literal("")),
});

type ContactInput = z.infer<typeof contactSchema>;

export function LandingPage() {
  return (
    <>
      <Hero />
      <LogoStrip />
      <StatsBand />
      <TwoTracks />
      <HowItWorks />
      <WhyUs />
      <ForEngineers />
      <About />
      <Contact />
    </>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden px-4 py-18 sm:px-6 sm:py-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1100px 520px at 18% -10%, rgba(91,141,239,0.18), transparent 60%), radial-gradient(900px 480px at 92% 0%, rgba(167,139,250,0.18), transparent 60%), radial-gradient(700px 420px at 60% 100%, rgba(74,222,128,0.10), transparent 55%), #FBFCFE",
        }}
      />
      <div className="relative mx-auto max-w-[1100px] text-center">
        <span className="mb-5 inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border border-border bg-surface/70 px-3.5 py-1.5 pl-2 text-[13px] shadow-sm">
          <span className="grid size-[22px] place-items-center rounded-full bg-success-soft text-[11px] text-[#166534]">
            ●
          </span>
          Established in 2020 in Yerevan, Armenia
        </span>
        <h1 className="mx-auto max-w-[920px] text-[clamp(40px,6vw,64px)] font-bold leading-[1.05] tracking-[-0.025em]">
          Senior IT specialists,
          <br />
          on your team in{" "}
          <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent">
            48 hours
          </span>
          .
        </h1>
        <p className="mx-auto mt-4 max-w-[640px] text-lg leading-relaxed text-text-muted">
          We match growing software companies with vetted engineers — full-time,
          on your stack, ready to ship. Outstaffing or direct hire, your call.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3.5">
          <CtaCard
            to="/hire"
            kicker="Companies"
            title="Looking for a specialist"
            gradient="from-primary to-[#6F70F1]"
          />
          <CtaCard
            to="/jobs"
            kicker="Engineers"
            title="Looking for a job"
            gradient="from-success to-primary"
          />
        </div>
      </div>
    </section>
  );
}

function CtaCard({
  to,
  kicker,
  title,
  gradient,
}: {
  to: string;
  kicker: string;
  title: string;
  gradient: string;
}) {
  return (
    <Link
      to={to}
      className="flex w-full max-w-[360px] items-center gap-3.5 rounded-2xl border border-border bg-surface p-3.5 pr-5 text-text shadow-md transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-lg sm:min-w-[280px]"
    >
      <span
        className={`grid size-11 place-items-center rounded-xl bg-gradient-to-br ${gradient} text-white`}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </span>
      <span className="flex flex-col text-left">
        <span className="text-xs text-text-muted">{kicker}</span>
        <span className="flex items-center gap-1.5 text-[15px] font-semibold">
          {title}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </span>
      </span>
    </Link>
  );
}

function LogoStrip() {
  return (
    <section className="border-y border-border bg-surface px-6 py-7 text-center">
      <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-soft">
        Teams hiring through NgonTech
      </div>
      <div className="flex flex-wrap items-center justify-center gap-10 text-lg font-bold tracking-tight text-text-soft opacity-85">
        <span>◆ Acme Capital</span>
        <span>● Lumen Pay</span>
        <span>▲ Novato Health</span>
        <span>◇ Orbit Software</span>
        <span>◯ Solis Mobile</span>
        <span>✦ Rayon</span>
      </div>
    </section>
  );
}

function StatsBand() {
  const stats = [
    { num: "Top 3%", label: "of applicants pass our vetting bar" },
    { num: "48h", label: "from brief to first matched profile" },
    { num: "96%", label: "of placements stay past 6 months" },
  ];
  return (
    <section className="px-6 py-16">
      <div className="mx-auto flex max-w-[720px] flex-wrap justify-center gap-12 text-center">
        {stats.map((s) => (
          <div key={s.num}>
            <div className="bg-gradient-to-r from-primary to-accent bg-clip-text text-3xl font-bold tracking-tight text-transparent">
              {s.num}
            </div>
            <div className="mt-1 text-sm text-text-muted">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({
  kicker,
  title,
  lead,
}: {
  kicker: string;
  title: React.ReactNode;
  lead?: string;
}) {
  return (
    <div className="mx-auto mb-14 max-w-[720px] text-center">
      <span className="mb-3 inline-block text-[12px] font-semibold uppercase tracking-[0.12em] text-primary-hover">
        {kicker}
      </span>
      <h2 className="text-[clamp(28px,3.6vw,40px)] font-bold leading-tight tracking-[-0.02em]">
        {title}
      </h2>
      {lead && (
        <p className="mx-auto mt-3.5 max-w-[580px] text-base leading-relaxed text-text-muted">
          {lead}
        </p>
      )}
    </div>
  );
}

function TwoTracks() {
  return (
    <section className="border-y border-border bg-surface px-6 py-24">
      <div className="mx-auto max-w-[1100px]">
        <SectionHead
          kicker="Two ways to engage"
          title="One platform. Outstaffing & recruiting."
          lead="Pick the engagement model that matches what you're building. We'll bring the right people either way."
        />
        <div className="grid gap-5 md:grid-cols-2">
          <Track
            title="Outstaffing"
            body="The specialist works full-time on your team while staying on our payroll. You own the work and roadmap; we handle contracts, taxes, equipment and HR."
            bullets={[
              "Predictable monthly rate",
              "Scale up or down month by month",
              "No payroll, visa or admin overhead",
              "Replacement guarantee in 14 days",
            ]}
            cta="Request outstaffing"
          />
          <Track
            alt
            title="Recruiting"
            body="We source and shortlist candidates for permanent in-house roles. You hire them directly. One-time placement fee — no ongoing commitment."
            bullets={[
              "Direct employee on your payroll",
              "Flat fee, paid on signed offer",
              "Pre-screened, calibrated to your bar",
              "90-day replacement guarantee",
            ]}
            cta="Request recruiting"
          />
        </div>
      </div>
    </section>
  );
}

function Track({
  title,
  body,
  bullets,
  cta,
  alt,
}: {
  title: string;
  body: string;
  bullets: string[];
  cta: string;
  alt?: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface p-9 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 size-56 rounded-full"
        style={{
          background: alt
            ? "radial-gradient(circle, rgba(74,222,128,0.16), transparent 70%)"
            : "radial-gradient(circle, rgba(91,141,239,0.16), transparent 70%)",
        }}
      />
      <h3 className="mb-2 text-[22px] font-bold tracking-tight">{title}</h3>
      <p className="mb-5 text-[15px] leading-relaxed text-text-muted">{body}</p>
      <ul className="mb-6 flex flex-col gap-2 text-sm text-text">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={alt ? "#16A34A" : "var(--primary)"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {b}
          </li>
        ))}
      </ul>
      <Link
        to="/hire"
        className={buttonClass({ variant: "primary" })}
        style={alt ? { background: "#16A34A" } : undefined}
      >
        {cta}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Send a brief", body: "Tell us about the role, stack and engagement model. Five minutes — no account needed." },
    { n: "02", title: "Meet a shortlist", body: "Within 48 hours we share 3–5 vetted profiles. You interview the ones you like." },
    { n: "03", title: "Start shipping", body: "Your new specialist joins the team — onboarded, equipped, and ready to commit." },
  ];
  return (
    <section id="how" className="px-6 py-24">
      <div className="mx-auto max-w-[1100px]">
        <SectionHead
          kicker="How it works"
          title="From brief to first commit in three steps."
          lead="Most teams are interviewing matched candidates within 48 hours, and shipping with them inside two weeks."
        />
        <div className="grid gap-4 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="rounded-xl border border-border bg-surface p-6">
              <div className="mb-3.5 grid size-8 place-items-center rounded-md bg-accent-soft text-sm font-bold text-[#6D28D9]">
                {s.n}
              </div>
              <h4 className="mb-1.5 text-[15px] font-semibold">{s.title}</h4>
              <p className="text-[13px] leading-relaxed text-text-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const reasons = [
    { title: "Vetted, not just listed", body: "Every specialist passes a 4-stage screen: profile, technical, project review, and a delivery interview." },
    { title: "Fast, by design", body: "You'll have a first matched profile in under 48 hours, and a shortlist within 3 business days." },
    { title: "Real humans, not chatbots", body: "One delivery lead owns your account end-to-end — from first call to month-six check-in." },
    { title: "Transparent pricing", body: "Flat monthly rates for outstaffing, fixed placement fee for recruiting. No margin games." },
    { title: "Risk-free trial", body: "If a match isn't working in the first 14 days, we replace at no charge — or refund." },
    { title: "European time-zone", body: "Most of our network sits in CET ±3h, so daily standups actually happen at standup time." },
  ];
  return (
    <section className="border-y border-border bg-surface px-6 py-24">
      <div className="mx-auto max-w-[1100px]">
        <SectionHead
          kicker="Why teams pick us"
          title="A small, opinionated network — not a marketplace."
          lead="We talk to every engineer before they reach you. That's why our acceptance rate is high and our turnover is low."
        />
        <div className="grid gap-4 md:grid-cols-3">
          {reasons.map((r) => (
            <div
              key={r.title}
              className="rounded-2xl border border-border bg-surface p-6 transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
            >
              <h4 className="mb-1.5 text-base font-semibold">{r.title}</h4>
              <p className="text-sm leading-relaxed text-text-muted">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForEngineers() {
  return (
    <section
      className="border-y px-6 py-24"
      style={{
        borderColor: "#EDE7FA",
        background:
          "radial-gradient(700px 360px at 80% 0%, rgba(167,139,250,0.20), transparent 60%), radial-gradient(600px 360px at 0% 100%, rgba(91,141,239,0.16), transparent 60%), #FBFAFF",
      }}
    >
      <div className="mx-auto grid max-w-[1100px] items-center gap-14 md:grid-cols-2">
        <div>
          <span className="mb-3 inline-block text-[12px] font-semibold uppercase tracking-[0.12em] text-[#6D28D9]">
            For engineers
          </span>
          <h2 className="mb-3.5 text-[clamp(28px,3.6vw,40px)] font-bold leading-tight tracking-[-0.02em]">
            Better gigs.{" "}
            <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
              Without the grind.
            </span>
          </h2>
          <p className="mb-6 text-[15px] leading-relaxed text-text-muted">
            Stop trawling job boards and writing the same cover letter twelve times.
            Apply once — we line up roles that actually match your stack, salary and timezone.
          </p>
          <Link
            to="/jobs"
            className={`${buttonClass({ size: "md" })} bg-[#6D28D9] hover:bg-[#5B21B6]`}
          >
            Browse open roles
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="scroll-mt-20 px-6 py-24">
      <div className="mx-auto grid max-w-[1100px] items-center gap-14 md:grid-cols-[1.1fr_1fr]">
        <div>
          <span className="mb-3 inline-block text-[12px] font-semibold uppercase tracking-[0.12em] text-primary-hover">
            About NgonTech
          </span>
          <h2 className="mb-4 text-[clamp(28px,3.6vw,40px)] font-bold leading-tight tracking-[-0.02em]">
            We're a small team that thinks staffing is broken.
          </h2>
          <p className="mb-3.5 text-[15px] leading-relaxed text-text-muted">
            Most agencies optimise for volume — pile up CVs, hope something sticks, collect a margin.
            We chose the opposite: a curated network, deep relationships, and a delivery lead who
            actually knows your stack.
          </p>
          <p className="mb-5 text-[15px] leading-relaxed text-text-muted">
            NGON Tech was established in 2020 in Yerevan, Armenia. Today we work with software teams across Europe in
            fintech, payments, healthtech, and SaaS.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            ["2020", "Established in Yerevan"],
            ["14", "Countries we recruit from"],
            ["200+", "Specialists placed"],
            ["96%", "6-month retention"],
          ].map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-border bg-surface p-5">
              <div className="bg-gradient-to-r from-primary to-accent bg-clip-text text-3xl font-bold tracking-tight text-transparent">
                {v}
              </div>
              <div className="mt-1 text-[13px] text-text-muted">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="scroll-mt-20 border-t border-border bg-surface px-6 py-24">
      <div className="mx-auto max-w-[1100px]">
        <SectionHead
          kicker="Get in touch"
          title="Tell us what you're building."
          lead="Drop a line. A delivery lead — not a sales bot — replies within one business day."
        />
        <div className="grid items-stretch gap-8 md:grid-cols-[1fr_1.3fr]">
          <ContactInfo />
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <div className="relative flex flex-col gap-5 overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-accent p-9 text-white">
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -right-20 size-64 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)" }}
      />
      <h3 className="text-[22px] font-bold tracking-tight">Talk to a real human.</h3>
      <p className="text-sm leading-relaxed opacity-90">
        For hiring questions, partnership ideas, or anything else.
      </p>
      <div className="relative z-10 flex items-center gap-3 text-sm">
        <span className="grid size-9 place-items-center rounded-md bg-white/20">@</span>
        <span>info@ngon.digital</span>
      </div>
      <div className="relative z-10 flex items-center gap-3 text-sm">
        <span className="grid size-9 place-items-center rounded-md bg-white/20">📞</span>
        <span>+1 316 218 6504</span>
      </div>
    </div>
  );
}

function ContactForm() {
  const submit = useSubmitContact();
  const [done, setDone] = useState(false);
  const [topError, setTopError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: { interest: "outstaffing", website: "" },
  });

  async function onSubmit(values: ContactInput) {
    setTopError("");
    if (values.website) {
      // Honeypot tripped — silently succeed.
      setDone(true);
      return;
    }
    try {
      await submit.mutateAsync({
        name: values.name,
        email: values.email,
        company: values.company || null,
        interest: values.interest,
        message: values.message,
        website: null,
        turnstile_token: null,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setTopError("Too many submissions — try again in a minute.");
        else setTopError(err.message || "Could not send. Please try again.");
      } else {
        setTopError("Network error. Please try again.");
      }
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-8 text-center">
        <h3 className="text-xl font-bold">Message sent</h3>
        <p className="mt-2 text-text-muted">Thanks — we reply within one business day.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="rounded-2xl border border-border bg-surface p-8"
    >
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        {...register("website")}
        className="absolute -left-[9999px]"
        aria-hidden
      />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Your name" required error={errors.name?.message}>
          <input
            type="text"
            placeholder="e.g. Anna Chen"
            {...register("name")}
            className={fieldInputClass(!!errors.name)}
          />
        </Field>
        <Field label="Work email" required error={errors.email?.message}>
          <input
            type="email"
            placeholder="anna@yourcompany.com"
            {...register("email")}
            className={fieldInputClass(!!errors.email)}
          />
        </Field>
        <Field label="Company">
          <input
            type="text"
            placeholder="Acme Capital"
            {...register("company")}
            className={fieldInputClass()}
          />
        </Field>
        <Field label="I'm interested in">
          <select {...register("interest")} className={fieldInputClass()}>
            <option value="outstaffing">Outstaffing</option>
            <option value="recruiting">Recruiting</option>
            <option value="both">Both / not sure yet</option>
            <option value="job">Looking for a job</option>
          </select>
        </Field>
        <Field
          label="Message"
          required
          error={errors.message?.message}
          className="sm:col-span-2"
        >
          <textarea
            placeholder="What are you building, and what kind of help would unlock the next milestone?"
            {...register("message")}
            className={`${fieldInputClass(!!errors.message)} min-h-[110px] resize-y`}
          />
        </Field>
      </div>
      {topError && (
        <div className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-[#b91c1c]">
          {topError}
        </div>
      )}
      <div className="mt-5 flex items-center justify-end gap-3 border-t border-border pt-4">
        <span className="mr-auto text-xs text-text-muted">
          We reply within one business day.
        </span>
        <Button type="submit" loading={isSubmitting}>
          Send message
        </Button>
      </div>
    </form>
  );
}
